/** @file Removes one placeholder mapping of a slot from a skin (the attachment itself is kept). */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import type { Doc, Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { skinDraft } from './skinDraft';

/** INFO `{ attachment }` at `placeholder` when no skin maps `attId` any more; the orphan is kept (W2-31). */
function warnOrphan(doc: Doc, attId: Id, ctx: CommandContext): void {
  const att = doc.attachments[attId];
  if (!att || Object.values(doc.skins).some((s) => s.entries.some((e) => e.attachment === attId))) return;
  const message = `attachment "${att.name}" is now in no skin; it is kept (skin.setEntry maps it again, attachment.delete removes it)`;
  ctx.warnings.push({ code: 'INFO', message, params: { attachment: att.name }, path: 'placeholder' });
}

/** skin.removeEntry: drops one mapping (INFO and no change when none matches) and keeps the attachment (W2-31). */
export const command = defineCommand({
  name: 'skin.removeEntry',
  description:
    'Remove the mapping of `placeholder` on `slot` from `skin` (the default skin when omitted); the attachment is kept even when no skin maps it any more (an INFO says so; attachment.delete removes it).',
  input: z.object({ skin: z.string().min(1).exactOptional(), slot: z.string().min(1), placeholder: prim.name }).strict(),
  apply(draft, input, ctx) {
    const skin = skinDraft(draft, ctx, input.skin);
    const slot = ctx.resolve('slot', input.slot);
    const at = skin.entries.findIndex((e) => e.slot === slot && e.placeholder === input.placeholder);
    const removed = skin.entries[at]?.attachment;
    if (removed === undefined) {
      ctx.warnings.push({ code: 'INFO', message: `no entry maps "${input.placeholder}" on this slot; nothing was removed`, path: 'placeholder' });
      return;
    }
    skin.entries = skin.entries.filter((_, i) => i !== at);
    warnOrphan(draft, removed, ctx);
    draft.meta.updatedAt = ctx.now;
  },
});
